import { supabase } from "./client";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "../local/runtimeEnvironment";

const MZ_PACKAGE_PAYMENT_FUNCTION_NAME = "mp-create-mz-package-payment";
const MZ_PACKAGE_LIST_FUNCTION_NAME = "mz-list-credit-packages";
const MZ_PACKAGE_STATUS_FUNCTION_NAME = "mp-mz-package-webhook";

export const DEFAULT_MZ_CREDIT_PACKAGES = Object.freeze([
  {
    id: "inicio",
    packageCode: "inicio",
    packageName: "Pack Inicio",
    name: "Pack Inicio",
    description: "Entrada para restaurantes que quieren probar kiosk con saldo operativo inicial.",
    unitsAmount: 50000,
    units: 50000,
    priceArs: 70000,
    price: 70000,
    enabled: true,
    sortOrder: 10,
  },
  {
    id: "local",
    packageCode: "local",
    packageName: "Pack Local",
    name: "Pack Local",
    description: "Pack intermedio para locales que ya usan kiosk de forma sostenida.",
    unitsAmount: 150000,
    units: 150000,
    priceArs: 200000,
    price: 200000,
    enabled: true,
    sortOrder: 20,
  },
  {
    id: "alto-flujo",
    packageCode: "alto-flujo",
    packageName: "Pack Alto flujo",
    name: "Pack Alto flujo",
    description: "Bolsa grande para restaurantes con venta alta desde kiosk.",
    unitsAmount: 400000,
    units: 400000,
    priceArs: 500000,
    price: 500000,
    enabled: true,
    sortOrder: 30,
  },
  {
    id: "empresa",
    packageCode: "empresa",
    packageName: "Pack Empresa",
    name: "Pack Empresa",
    description: "Pack para empresas o grupos con varias sucursales y alto volumen mensual.",
    unitsAmount: 1000000,
    units: 1000000,
    priceArs: 1150000,
    price: 1150000,
    enabled: true,
    sortOrder: 40,
  },
]);

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const toSafeInteger = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
};

const resolveCheckoutPayload = (value = {}) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  if (value.checkout && typeof value.checkout === "object") {
    return value.checkout;
  }

  if (value.data && typeof value.data === "object") {
    return value.data;
  }

  return value;
};

const resolveCheckoutUrl = (value = {}) => {
  const payload = resolveCheckoutPayload(value);
  const providerPayload =
    payload.providerPayload && typeof payload.providerPayload === "object"
      ? payload.providerPayload
      : {};

  return toSafeText(
    payload.checkoutUrl ||
      payload.checkout_url ||
      payload.initPoint ||
      payload.init_point ||
      payload.preferenceUrl ||
      payload.preference_url ||
      payload.linkUrl ||
      payload.link_url ||
      payload.url ||
      providerPayload.checkoutUrl ||
      providerPayload.checkout_url ||
      providerPayload.init_point ||
      providerPayload.initPoint ||
      "",
    1400
  );
};

const resolveQrPayload = (value = {}) => {
  const payload = resolveCheckoutPayload(value);
  const providerPayload =
    payload.providerPayload && typeof payload.providerPayload === "object"
      ? payload.providerPayload
      : {};
  const qrPayload =
    payload.qr && typeof payload.qr === "object"
      ? payload.qr
      : providerPayload.qr && typeof providerPayload.qr === "object"
        ? providerPayload.qr
        : {};

  return {
    qrData: toSafeText(
      payload.qrData ||
        payload.qr_data ||
        payload.copyCode ||
        payload.copy_code ||
        qrPayload.qrData ||
        qrPayload.qr_data ||
        qrPayload.copyCode ||
        qrPayload.copy_code ||
        "",
      4000
    ),
    copyCode: toSafeText(
      payload.copyCode ||
        payload.copy_code ||
        payload.qrData ||
        payload.qr_data ||
        qrPayload.copyCode ||
        qrPayload.copy_code ||
        qrPayload.qrData ||
        qrPayload.qr_data ||
        "",
      4000
    ),
    imageUrl: toSafeText(
      payload.imageUrl ||
        payload.image_url ||
        qrPayload.imageUrl ||
        qrPayload.image_url ||
        qrPayload.image ||
        providerPayload.imageUrl ||
        providerPayload.image_url ||
        "",
      1400
    ),
    externalPosId: toSafeText(
      payload.externalPosId ||
        payload.external_pos_id ||
        qrPayload.externalPosId ||
        qrPayload.external_pos_id ||
        "",
      160
    ),
  };
};

export const normalizeMzCreditPackage = (value = {}, fallbackIndex = 0) => {
  const packageCode = toSafeText(
    value.packageCode || value.package_code || value.id || "",
    80
  );
  const packageName = toSafeText(
    value.packageName || value.package_name || value.name || "Pack MZ",
    120
  );
  const unitsAmount = toSafeInteger(value.unitsAmount ?? value.units_amount ?? value.units);
  const priceArs = toMoney(value.priceArs ?? value.price_ars ?? value.price);

  return {
    id: toSafeText(value.id || packageCode || `mz-pack-${fallbackIndex}`, 100),
    packageCode,
    packageName,
    name: packageName,
    description: toSafeText(value.description || "", 280),
    unitsAmount,
    units: unitsAmount,
    priceArs,
    price: priceArs,
    enabled: value.enabled !== false,
    sortOrder: toSafeInteger(value.sortOrder ?? value.sort_order ?? (fallbackIndex + 1) * 10),
  };
};

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = toSafeText(error?.message, 240);
  const backendMessage = toSafeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );

  if (backendMessage && transportMessage.toLowerCase().includes("non-2xx")) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = toSafeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = toSafeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

export const normalizeMzCreditPackageCheckout = (value = {}) => {
  const payload = resolveCheckoutPayload(value);
  const qrPayload = resolveQrPayload(payload);

  return {
    id: toSafeText(
      payload.id || payload.checkoutId || payload.checkout_id || "",
      160
    ),
    provider: toSafeText(payload.provider || "mercado_pago", 40).toLowerCase(),
    status: toSafeText(payload.status || "checkout_ready", 40).toLowerCase(),
    externalReference: toSafeText(
      payload.externalReference || payload.external_reference || "",
      160
    ),
    providerPreferenceId: toSafeText(
      payload.providerPreferenceId ||
        payload.provider_preference_id ||
        payload.providerOrderId ||
        payload.provider_order_id ||
        payload.providerOperationId ||
        payload.provider_operation_id ||
        "",
      160
    ),
    providerOrderId: toSafeText(
      payload.providerOrderId ||
        payload.provider_order_id ||
        "",
      160
    ),
    providerPaymentHintId: toSafeText(
      payload.providerPaymentHintId ||
        payload.provider_payment_hint_id ||
        "",
      160
    ),
    checkoutUrl: resolveCheckoutUrl(payload),
    qrData: qrPayload.qrData,
    copyCode: qrPayload.copyCode,
    imageUrl: qrPayload.imageUrl,
    externalPosId: qrPayload.externalPosId,
    packageCode: toSafeText(
      payload.packageCode || payload.package_code || "",
      80
    ),
    packageName: toSafeText(
      payload.packageName || payload.package_name || "",
      120
    ),
    unitsAmount: toSafeInteger(
      payload.unitsAmount ?? payload.units_amount ?? payload.units
    ),
    priceArs: toMoney(payload.priceArs ?? payload.price_ars ?? payload.price),
    branchId: toSafeText(payload.branchId || payload.branch_id || "", 80),
    expiresAt: toSafeText(payload.expiresAt || payload.expires_at || "", 80),
    raw: value,
  };
};

export const listEnabledMzCreditPackages = async () => {
  if (!supabase) {
    return DEFAULT_MZ_CREDIT_PACKAGES;
  }

  const { data, error } = await supabase.functions.invoke(
    MZ_PACKAGE_LIST_FUNCTION_NAME,
    {
      body: {},
    }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos leer los packs MZ disponibles."
      )
    );
  }

  const rawPackages = Array.isArray(data?.packages)
    ? data.packages
    : Array.isArray(data)
      ? data
      : [];
  const packages = rawPackages
    .map((pkg, index) => normalizeMzCreditPackage(pkg, index))
    .filter((pkg) => pkg.enabled && pkg.packageCode && pkg.unitsAmount > 0 && pkg.priceArs > 0)
    .sort((left, right) => left.sortOrder - right.sortOrder);

  return packages.length > 0 ? packages : DEFAULT_MZ_CREDIT_PACKAGES;
};

export const createMzCreditPackageCheckout = async ({
  companyId,
  branchId = "",
  packageCode,
  returnUrl = "",
} = {}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.PAYMENT_OPERATIONS);

  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos iniciar la compra de MZ."
    );
  }

  const normalizedCompanyId = toSafeText(companyId, 80);
  const normalizedPackageCode = toSafeText(packageCode, 80);

  if (!normalizedCompanyId || !normalizedPackageCode) {
    throw new Error("Faltan datos para iniciar la compra del pack MZ.");
  }

  const { data, error } = await supabase.functions.invoke(
    MZ_PACKAGE_PAYMENT_FUNCTION_NAME,
    {
      body: {
        companyId: normalizedCompanyId,
        branchId: toSafeText(branchId, 80),
        packageCode: normalizedPackageCode,
        returnUrl:
          toSafeText(returnUrl, 1400) ||
          (typeof window !== "undefined"
            ? toSafeText(window.location.href, 1400)
            : ""),
      },
    }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos crear el checkout de Mercado Pago para comprar MZ."
      )
    );
  }

  const checkout = normalizeMzCreditPackageCheckout(data);

  if (!checkout.checkoutUrl && !checkout.qrData && !checkout.imageUrl) {
    throw new Error(
      "Mercado Pago no devolvio un QR ni un link valido para comprar el pack MZ."
    );
  }

  return checkout;
};

export const checkMzCreditPackageCheckoutStatus = async ({
  companyId,
  branchId = "",
  packageCode = "",
  externalReference,
  providerOrderId = "",
  paymentId = "",
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos validar el pago del pack MZ."
    );
  }

  const normalizedCompanyId = toSafeText(companyId, 80);
  const normalizedExternalReference = toSafeText(externalReference, 160);

  if (!normalizedCompanyId || !normalizedExternalReference) {
    throw new Error(
      "Faltan datos para revisar el estado del cobro del pack MZ."
    );
  }

  const { data, error } = await supabase.functions.invoke(
    MZ_PACKAGE_STATUS_FUNCTION_NAME,
    {
      body: {
        companyId: normalizedCompanyId,
        branchId: toSafeText(branchId, 80),
        packageCode: toSafeText(packageCode, 80),
        externalReference: normalizedExternalReference,
        providerOrderId: toSafeText(providerOrderId, 160),
        paymentId: toSafeText(paymentId, 160),
      },
    }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos revisar el estado del cobro del pack MZ."
      )
    );
  }

  return {
    ok: data?.ok === true,
    pending: data?.pending === true,
    approved: data?.approved === true,
    alreadyCredited: data?.alreadyCredited === true,
    transactionId: toSafeText(data?.transactionId, 160),
    balanceAfterUnits: toSafeInteger(data?.balanceAfterUnits),
    paymentId: toSafeText(data?.paymentId, 160),
    providerOrderId: toSafeText(data?.providerOrderId || providerOrderId, 160),
    externalReference: toSafeText(
      data?.externalReference || normalizedExternalReference,
      160
    ),
    packageCode: toSafeText(data?.packageCode || packageCode, 80),
    unitsAmount: toSafeInteger(data?.unitsAmount),
  };
};
